/**
 * Formatação — pt-BR, 24h, dd/mm.
 *
 * Dinheiro em unidades menores (centavos) sempre. Nenhuma função aqui aceita
 * `number` em unidade maior: converter cedo demais é como erros de centavo
 * entram num sistema financeiro.
 *
 * A moeda vem SEMPRE por parâmetro. Não existe padrão — é a viagem que define
 * (regra de genericidade).
 */

import { type CalendarDate, toEpochDay } from './calendar.js';

/**
 * Valor monetário a partir de unidades menores.
 *
 * `minorUnitsPerUnit` é 100 para as moedas do produto. Fica explícito porque
 * nem toda moeda tem duas casas, e assumir isso silenciosamente quebraria
 * numa expansão comercial.
 */
export function formatMoney(
  amountMinor: number,
  currency: string,
  options: {
    locale?: string;
    minorUnitsPerUnit?: number;
    showSign?: boolean;
    /**
     * Esconde os centavos. Usado nos cards de métrica, onde o valor é
     * tipografia grande e os centavos estouram a largura — o protótipo
     * aprovado mostra "US$ 1.428" ali, e o valor exato no detalhe.
     */
    hideCents?: boolean;
  } = {},
): string {
  const {
    locale = 'pt-BR',
    minorUnitsPerUnit = 100,
    showSign = false,
    hideCents = false,
  } = options;

  const casas = hideCents ? 0 : 2;

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    signDisplay: showSign ? 'exceptZero' : 'auto',
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  }).format(amountMinor / minorUnitsPerUnit);
}

/** `dd/mm` — o formato do design. */
export function formatDayMonth(date: CalendarDate): string {
  const [, month, day] = date.split('-') as [string, string, string];
  return `${day}/${month}`;
}

const MESES_CURTOS = [
  'JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN',
  'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ',
] as const;

/** `27 OUT` — usado no cabeçalho de datas da viagem. */
export function formatDayMonthShort(date: CalendarDate): string {
  const [, month, day] = date.split('-') as [string, string, string];
  const mes = MESES_CURTOS[Number(month) - 1] ?? '';
  return `${Number(day)} ${mes}`;
}

/** `27 OUT → 18 NOV` */
export function formatDateRange(start: CalendarDate, end: CalendarDate): string {
  return `${formatDayMonthShort(start)} → ${formatDayMonthShort(end)}`;
}

/**
 * Plural em pt-BR para as contagens da interface.
 *
 * Existe para que a Home não escreva "1 dias" nem "Faltam 1 dias" — erro que
 * aparece exatamente uma vez por viagem, na véspera, quando todo mundo olha.
 */
export function pluralize(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** "Faltam 51 dias" · "Falta 1 dia" · "É hoje" */
export function formatCountdown(days: number): string {
  if (days === 0) return 'É hoje';
  if (days === 1) return 'Falta 1 dia';
  return `Faltam ${days} dias`;
}

/** Tempo relativo curto, como nas etiquetas de pin do mapa. */
export function formatRelativeMinutes(minutes: number): string {
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `${minutes} min`;
  const horas = Math.floor(minutes / 60);
  return horas === 1 ? '1 h' : `${horas} h`;
}

/** Diferença em dias entre duas datas civis, para rótulos de "há X dias". */
export function daysBetween(from: CalendarDate, to: CalendarDate): number {
  return toEpochDay(to) - toEpochDay(from);
}
